// WiserWeb account SDK — the shared login/signup crypto orchestration, lifted from wiser-office's
// sdk/account.ts (the app-state-free core) so every suite app derives keys and proves the login verifier
// IDENTICALLY. Composes the shared crypto (@wiser/e2e) with the canonical account service (@wiser/accounts)
// so the SINGLE WiserWeb account — usable across office / mail / vault / dns / … — logs in the same way.
//
// CONTENT-BLIND: the password never leaves the device. We derive (Argon2id, domain-separated salts) an
// auth verifier (SHA-256(auth_key)) for the server and a KEK to unwrap the master key locally; the master
// key unwraps the private key. `unlock()` works OFFLINE from cached blobs — for apps that persist a session.
import {
  deriveKEK, generateMasterKey, wrapMasterKey, unwrapMasterKey,
  generateKeypair, wrapPrivateKey, unwrapPrivateKey, toBase64, fromBase64,
} from '@wiser/e2e';
import type { AccountsApi, AccountBlobs, SessionInfo, ChangeEmailInput } from '@wiser/accounts';
import { isNewDeviceChallenge } from '@wiser/accounts';
import { getOrCreateDeviceId } from './deviceId';
export { EmailNotVerifiedError } from '@wiser/accounts';
export type { SessionInfo } from '@wiser/accounts';
// Re-exported for compat: the device id lives in ./deviceId now (pluggable store — localStorage on web,
// injectable on React Native). NOTE: async since the store seam landed.
export { getOrCreateDeviceId };

/** Thrown by `logIn` when the password is correct but the account gates unrecognised devices (0015): the
 *  service has emailed a one-time PIN. Catch it, collect the PIN, and call `completeDeviceChallenge`. */
export class DeviceChallengeRequiredError extends Error {
  constructor(public email: string) {
    super('new device challenge required');
    this.name = 'DeviceChallengeRequiredError';
  }
}

/** The unwrapped key material — held only in memory while the account is unlocked. Auth-only apps (that
 *  don't encrypt data) can ignore these entirely and use only `token`/`account`. */
export interface UnlockedKeys {
  masterKey: Uint8Array;
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}
export interface UnlockedAccount {
  /** Opaque session token from wiser-accounts. Trade it for your app's native session (e.g. POST /auth/wiser). */
  token: string;
  /** Server-returned blobs — CACHE these on-device for offline `unlock()` (crypto apps only). */
  account: AccountBlobs;
  keys: UnlockedKeys;
  /** Present only when the sign-in opted into "keep me signed in" (0017): a revocable refresh token to store
   *  in the OS keychain (see secretStore.ts) and later trade via `resumeSession`. */
  refreshToken?: string;
}

/** A session restored on launch from a stored refresh token — IDENTITY only (no keys; a crypto app still
 *  unlocks the vault with the password). Carries the ROTATED refresh token to persist in place of the old one. */
export interface ResumedSession {
  token: string;
  account: AccountBlobs;
  refreshToken: string;
}

/** Result of `signUp`. The account is created UNVERIFIED — no session is minted; the user must click the
 *  emailed verification link before they can log in. */
export interface SignUpResult {
  /** Always true — the email-verification step is mandatory. */
  verificationRequired: true;
  /** The email a verification link was sent to. */
  email: string;
  /** Locally-derived key material (NOT yet usable to unlock — there is no cached blob until first login). */
  keys: UnlockedKeys;
  /** The one-time RECOVERY CODE, generated + registered at signup (unless `setupRecovery: false`). Show it
   *  to the user ONCE and tell them to store it — it's the only way to reset a forgotten password without
   *  losing encrypted data. Undefined if recovery setup was skipped or (best-effort) failed. */
  recoveryCode?: string;
}

const enc = new TextEncoder();

async function sha256(data: Uint8Array): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.digest('SHA-256', data as unknown as ArrayBuffer));
}
function concat(a: Uint8Array, b: Uint8Array): Uint8Array {
  const out = new Uint8Array(a.length + b.length);
  out.set(a, 0); out.set(b, a.length);
  return out;
}
/** Domain-separate the one stored salt into independent Argon2 salts for the KEK vs. the auth key. These
 *  purpose strings are a WIRE CONTRACT shared with every suite app — do NOT change them. */
function purposeSalt(purpose: string, salt: Uint8Array): Promise<Uint8Array> {
  return sha256(concat(enc.encode(purpose), salt));
}

async function deriveKek(password: string, salt: Uint8Array): Promise<Uint8Array> {
  return deriveKEK(password, await purposeSalt('wiser-account/kek/v1', salt));
}
/** The login verifier the server stores/compares: SHA-256(auth_key). */
async function deriveVerifier(password: string, salt: Uint8Array): Promise<Uint8Array> {
  const authKey = await deriveKEK(password, await purposeSalt('wiser-account/auth/v1', salt));
  return sha256(authKey);
}

function randomSalt(): Uint8Array {
  const s = new Uint8Array(16);
  crypto.getRandomValues(s);
  return s;
}

/** Create the WiserWeb account: generate the master key + keypair, wrap them, register with the service.
 *  The account is created UNVERIFIED — the service emails a verification link and does NOT mint a session,
 *  so the caller must NOT treat the user as signed in. */
export async function signUp(
  api: AccountsApi, email: string, password: string,
  opts: { displayName?: string; tenantId?: string; inviteCode?: string; setupRecovery?: boolean } = {},
): Promise<SignUpResult> {
  const salt = randomSalt();
  const [kek, verifier] = await Promise.all([deriveKek(password, salt), deriveVerifier(password, salt)]);
  const masterKey = generateMasterKey();
  const wrappedMk = await wrapMasterKey(masterKey, kek);
  const kp = generateKeypair();
  const wrappedPriv = await wrapPrivateKey(kp.privateKey, masterKey);

  await api.signup({
    email,
    display_name: opts.displayName,
    tenant_id: opts.tenantId,
    invite_code: opts.inviteCode,
    auth_verifier_b64: toBase64(verifier),
    kdf_salt_b64: toBase64(salt),
    wrapped_master_key_b64: toBase64(wrappedMk),
    public_key_b64: toBase64(kp.publicKey),
    wrapped_private_key_b64: toBase64(wrappedPriv),
  });

  // Register a recovery code so a forgotten password is recoverable (unless the host opted out). Best-effort:
  // the account already exists, so a failure here doesn't fail signup — the user can set one up later in
  // settings. We already hold the current verifier (proves the password), so no extra lookup is needed.
  let recoveryCode: string | undefined;
  if (opts.setupRecovery !== false) {
    try {
      recoveryCode = await registerRecovery(api, email, verifier, masterKey);
    } catch (err) {
      console.error('wiser-auth: recovery setup at signup failed (non-fatal)', err);
    }
  }

  return {
    verificationRequired: true,
    email,
    keys: { masterKey, publicKey: kp.publicKey, privateKey: kp.privateKey },
    recoveryCode,
  };
}

// ── recovery code ─────────────────────────────────────────────────────────────────────────────────────
// A recovery code lets a user reset a forgotten password WITHOUT losing their encrypted data: it derives a
// stable RECOVERY KEY that independently wraps the master key (a second wrap alongside the password's). The
// recovery key is derived from the code + the EMAIL (not the password's kdf_salt), so it survives password
// resets — the server keeps `recovery_verifier`/`recovery_wrapped` across a reset. Reset also requires an
// emailed one-time PIN (server-side, mailbox proof), so a leaked recovery code alone cannot reset.

/** Crockford base32 (no I/L/O/U) — unambiguous to read/type. */
const CROCKFORD = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/** Generate a fresh recovery code: 120 bits of entropy as Crockford base32, grouped for readability
 *  ("XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"). Save-once; shown to the user, never sent to the server. */
export function generateRecoveryCode(): string {
  const raw = new Uint8Array(15); // 120 bits → 24 base32 chars exactly
  crypto.getRandomValues(raw);
  let bits = 0, value = 0, out = '';
  for (const byte of raw) {
    value = (value << 8) | byte;
    bits += 8;
    while (bits >= 5) { out += CROCKFORD[(value >>> (bits - 5)) & 31]; bits -= 5; }
  }
  if (bits > 0) out += CROCKFORD[(value << (5 - bits)) & 31];
  return out.match(/.{1,4}/g)!.join('-');
}

/** Normalize user-entered codes: uppercase, map look-alikes (I/L→1, O→0), drop separators/other chars. Makes
 *  the derivation forgiving of formatting while staying deterministic (generation only emits Crockford chars). */
export function normalizeRecoveryCode(input: string): string {
  return input
    .toUpperCase()
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0')
    .split('')
    .filter((c) => CROCKFORD.includes(c))
    .join('');
}

/** LEGACY per-user salt for the recovery-key KDF: derived from the email (lowercased). Kept ONLY as a fallback
 *  for accounts whose recovery was set up before the salt was decoupled (server returns a null recovery_salt).
 *  New setups use a random `randomSalt()` stored server-side, so the recovery key no longer depends on the
 *  email and the code survives a change-email as well as a password reset. */
async function legacyRecoverySalt(email: string): Promise<Uint8Array> {
  return sha256(concat(enc.encode('wiser-account/recovery/v1'), enc.encode(email.trim().toLowerCase())));
}

/** Derive the 32-byte recovery key (Argon2id) from a recovery code + its (now email-independent) salt. */
async function deriveRecoveryKey(code: string, salt: Uint8Array): Promise<Uint8Array> {
  return deriveKEK(normalizeRecoveryCode(code), salt);
}

/** Core: mint a recovery code + a fresh random salt, wrap the master key under the derived recovery key, and
 *  register it (proving the current password via `verifier`). The salt is stored server-side so the key can be
 *  re-derived later WITHOUT the email. Returns the code to show once. */
async function registerRecovery(
  api: AccountsApi, email: string, currentVerifier: Uint8Array, masterKey: Uint8Array,
): Promise<string> {
  const code = generateRecoveryCode();
  const salt = randomSalt();
  const recoveryKey = await deriveRecoveryKey(code, salt);
  const recoveryVerifier = await sha256(recoveryKey);
  const recoveryWrapped = await wrapMasterKey(masterKey, recoveryKey);
  await api.setupRecovery({
    email,
    current_auth_verifier_b64: toBase64(currentVerifier),
    recovery_verifier_b64: toBase64(recoveryVerifier),
    recovery_wrapped_b64: toBase64(recoveryWrapped),
    recovery_salt_b64: toBase64(salt),
  });
  return code;
}

/** Set up (or REPLACE — rotation) the account's recovery code. Re-proves the current password, so pass the
 *  password the user just entered plus the in-memory master key. Returns the new code to show once. */
export async function setUpRecovery(
  api: AccountsApi, email: string, password: string, masterKey: Uint8Array,
): Promise<string> {
  const { kdf_salt_b64 } = await api.lookup(email);
  const verifier = await deriveVerifier(password, fromBase64(kdf_salt_b64));
  return registerRecovery(api, email, verifier, masterKey);
}

/** Whether the account already has a recovery code configured (for a settings indicator / backfill prompt). */
export async function recoveryConfigured(api: AccountsApi, email: string): Promise<boolean> {
  const { configured } = await api.recoveryStatus(email);
  return configured;
}

/** Begin a forgot-password reset: ask the server to email a one-time PIN. Always resolves (constant-shape —
 *  never reveals whether the email has an account). */
export async function requestPasswordReset(api: AccountsApi, email: string): Promise<void> {
  await api.resetRequest(email);
}

/** Complete a forgot-password reset with the recovery code + the emailed PIN. Recovers the master key via the
 *  recovery key, re-wraps it under the NEW password, and swaps the server's verifier/salt/wrapped-MK. The
 *  master key (and everything keyed off it — the private key wrap) is unchanged, so encrypted data survives;
 *  the recovery code itself stays valid for next time. Throws on a wrong code/PIN (generic — not an oracle). */
export async function resetPasswordWithRecovery(
  api: AccountsApi,
  args: { email: string; recoveryCode: string; pin: string; newPassword: string },
): Promise<void> {
  const email = args.email.trim();
  // Fetch the (non-secret) recovery salt so the recovery key can be re-derived WITHOUT the email. A legacy
  // account (server returns a null salt) falls back to the old email-derived salt.
  const { recovery_salt_b64 } = await api.recoveryStatus(email);
  const salt = recovery_salt_b64 ? fromBase64(recovery_salt_b64) : await legacyRecoverySalt(email);
  const recoveryKey = await deriveRecoveryKey(args.recoveryCode, salt);
  const recoveryVerifier = await sha256(recoveryKey);
  // Exchange the recovery verifier for the recovery-wrapped master key, then unwrap it locally.
  const { recovery_wrapped_b64 } = await api.recover(email, toBase64(recoveryVerifier));
  const masterKey = await unwrapMasterKey(fromBase64(recovery_wrapped_b64), recoveryKey);
  // Re-derive everything under the new password (fresh salt) and re-wrap the (unchanged) master key.
  const newSalt = randomSalt();
  const [newKek, newVerifier] = await Promise.all([
    deriveKek(args.newPassword, newSalt),
    deriveVerifier(args.newPassword, newSalt),
  ]);
  const newWrappedMk = await wrapMasterKey(masterKey, newKek);
  await api.recoverReset({
    email,
    recovery_verifier_b64: toBase64(recoveryVerifier),
    reset_pin: args.pin.trim(),
    new_auth_verifier_b64: toBase64(newVerifier),
    new_kdf_salt_b64: toBase64(newSalt),
    new_wrapped_master_key_b64: toBase64(newWrappedMk),
  });
}

/** Change the password of a SIGNED-IN, UNLOCKED account. Re-derives everything under the new password (fresh
 *  salt → new KEK + verifier) and re-wraps the in-memory master key under the new KEK; the master key + keypair
 *  are unchanged, so encrypted data stays openable and the recovery code (email-bound, not password-bound)
 *  stays valid. The CURRENT password is re-proved server-side via its verifier, so a wrong current password is
 *  rejected — pass the password the user just typed plus `masterKey` from currentKeys().masterKey.
 *
 *  IMPORTANT: a successful change REVOKES every existing session server-side (including the caller's token).
 *  The in-memory keys still work offline, but the next server call with the old token will 401 — the host
 *  should trade the just-entered new password for a fresh session (re-login) or send the user to sign in. */
export async function changePassword(
  api: AccountsApi, email: string, currentPassword: string, newPassword: string, masterKey: Uint8Array,
): Promise<void> {
  // Prove the current password against the account's current salt (lookup is not an existence oracle).
  const { kdf_salt_b64 } = await api.lookup(email);
  const currentVerifier = await deriveVerifier(currentPassword, fromBase64(kdf_salt_b64));
  // Re-derive under the new password (fresh salt) and re-wrap the (unchanged) master key.
  const newSalt = randomSalt();
  const [newKek, newVerifier] = await Promise.all([
    deriveKek(newPassword, newSalt),
    deriveVerifier(newPassword, newSalt),
  ]);
  const newWrappedMk = await wrapMasterKey(masterKey, newKek);
  await api.changePassword({
    email,
    current_auth_verifier_b64: toBase64(currentVerifier),
    new_auth_verifier_b64: toBase64(newVerifier),
    new_kdf_salt_b64: toBase64(newSalt),
    new_wrapped_master_key_b64: toBase64(newWrappedMk),
  });
}

/** Explicit sign-out: revoke the current session server-side so the token can't be reused. Best-effort — a
 *  network failure is swallowed (the token expires server-side regardless), so the caller can still clear
 *  local state unconditionally. Pair with the session store's `signOut()` (which only drops in-memory state):
 *  `await logOut(api, token); signOut();`. Idempotent server-side. */
export async function logOut(api: AccountsApi, sessionToken: string): Promise<void> {
  try {
    await api.logout(sessionToken);
  } catch (err) {
    console.error('wiser-auth: server logout failed (clear local session anyway)', err);
  }
}

// ── sessions ("this device" management) ───────────────────────────────────────────────────────────────

/** List the signed-in account's active sessions (metadata only — never any bearer token). The `current`
 *  flag marks the session making the call. */
export async function listSessions(api: AccountsApi, sessionToken: string): Promise<SessionInfo[]> {
  return api.listSessions(sessionToken);
}

/** Revoke one specific session by its `session_id` (from listSessions). Idempotent. */
export async function revokeSession(api: AccountsApi, sessionToken: string, sessionId: string): Promise<void> {
  await api.revokeSessions(sessionToken, { session_id: sessionId });
}

/** "Sign out everywhere else" — revoke every session for the account EXCEPT the one making this call. */
export async function signOutEverywhereElse(api: AccountsApi, sessionToken: string): Promise<void> {
  await api.revokeSessions(sessionToken, { all_others: true });
}

// ── change email ──────────────────────────────────────────────────────────────────────────────────────

/** Begin a change-email: ask the server to email a one-time PIN to the NEW address (mailbox-control proof).
 *  Always resolves (constant shape). Follow with `changeEmail()` using the PIN. */
export async function changeEmailRequest(
  api: AccountsApi, sessionToken: string, newEmail: string,
): Promise<void> {
  await api.changeEmailRequest(sessionToken, newEmail.trim());
}

export interface ChangeEmailArgs {
  sessionToken: string;
  /** The account's current login email (to derive the current-password verifier). */
  currentEmail: string;
  currentPassword: string;
  newEmail: string;
  /** The one-time PIN emailed to `newEmail` by changeEmailRequest. */
  pin: string;
  /** LEGACY-recovery accounts only (server reports recovery configured with a null salt): the recovery code +
   *  in-memory master key, so recovery is re-keyed under a fresh random salt and the code survives the change.
   *  Omit for new-style (decoupled) recovery or no recovery. */
  recoveryCode?: string;
  masterKey?: Uint8Array;
}

/** Complete a change-email: re-prove the current password, submit the emailed PIN, and (for a legacy-recovery
 *  account only) re-key recovery under a fresh random salt so the code keeps working. Because new-style
 *  recovery and the login crypto are both email-independent, nothing else is re-wrapped. Returns the fresh
 *  (new-email) blobs — the session stays valid (an identity change, not a credential change). Throws generically
 *  on a wrong password/PIN or a taken address. */
export async function changeEmail(api: AccountsApi, args: ChangeEmailArgs): Promise<AccountBlobs> {
  const currentEmail = args.currentEmail.trim();
  const { kdf_salt_b64 } = await api.lookup(currentEmail);
  const currentVerifier = await deriveVerifier(args.currentPassword, fromBase64(kdf_salt_b64));
  const input: ChangeEmailInput = {
    new_email: args.newEmail.trim(),
    change_pin: args.pin.trim(),
    current_auth_verifier_b64: toBase64(currentVerifier),
  };
  if (args.recoveryCode && args.masterKey) {
    // Re-key legacy recovery: derive the recovery key from the SAME code under a fresh random salt (so it no
    // longer depends on the email) and re-wrap the master key under it. The code stays the same for the user.
    const salt = randomSalt();
    const recoveryKey = await deriveRecoveryKey(args.recoveryCode, salt);
    input.recovery_verifier_b64 = toBase64(await sha256(recoveryKey));
    input.recovery_wrapped_b64 = toBase64(await wrapMasterKey(args.masterKey, recoveryKey));
    input.recovery_salt_b64 = toBase64(salt);
  }
  return api.changeEmail(args.sessionToken, input);
}

// ── delete account ────────────────────────────────────────────────────────────────────────────────────

/** Self-service account deletion (soft delete + grace-period purge). Re-proves the current password and
 *  requires the caller to confirm by passing the account's own email. The server flags the account, revokes
 *  all sessions immediately, and refuses login while flagged; a background job hard-deletes it after the grace
 *  window. IRREVERSIBLE once purged — the wrapped master key is gone, so encrypted data is unrecoverable. */
export async function deleteAccount(
  api: AccountsApi, args: { sessionToken: string; email: string; currentPassword: string },
): Promise<void> {
  const email = args.email.trim();
  const { kdf_salt_b64 } = await api.lookup(email);
  const verifier = await deriveVerifier(args.currentPassword, fromBase64(kdf_salt_b64));
  await api.deleteAccount(args.sessionToken, {
    current_auth_verifier_b64: toBase64(verifier),
    confirm_email: email,
  });
}

/** Sign in: look up the salt, prove the verifier, then unwrap the keys locally. The service refuses login
 *  (throwing `EmailNotVerifiedError`) until the email is verified — that error is allowed to propagate so the
 *  caller can offer a "resend verification" affordance rather than collapsing it into a generic failure.
 *
 *  NOTE: `lookup` is not an existence oracle (unknown emails return a decoy salt + 200); a wrong email or
 *  password therefore fails at `login` with a generic 401, which is exactly the intended behavior. */
export async function logIn(
  api: AccountsApi, email: string, password: string, keepSignedIn = false, client?: string,
): Promise<UnlockedAccount> {
  const { kdf_salt_b64 } = await api.lookup(email);
  const salt = fromBase64(kdf_salt_b64);
  const verifier = await deriveVerifier(password, salt);
  // `keepSignedIn` asks the service to also mint a refresh token (0017) for the OS keychain. `client` (0022)
  // is audit provenance only — the dashboard/app performing the sign-in (e.g. "selfserve", "console"); omitted
  // by default so it's off the wire for hosts that don't set it. NOT a service-usage signal.
  const res = await api.login(email, toBase64(verifier), await getOrCreateDeviceId(), keepSignedIn, client);
  // Gated account on an unrecognised device: a PIN was emailed — surface a typed error so the UI can prompt.
  if (isNewDeviceChallenge(res)) throw new DeviceChallengeRequiredError(res.email);
  const keys = await unlock(password, res.account);
  return { token: res.session_token, account: res.account, keys, refreshToken: res.refresh_token };
}

/** Restore a session on app launch from a stored refresh token (0017) — silent, no password. The token is
 *  ROTATED server-side, so persist the returned `refreshToken` in place of the old one. A rejected token
 *  (revoked by a password change / sign-out-everywhere, or expired) throws — the caller should clear the
 *  stored token and fall back to interactive sign-in. Identity only; a crypto app still unlocks with the pw. */
export async function resumeSession(api: AccountsApi, refreshToken: string): Promise<ResumedSession> {
  const res = await api.refreshSession(refreshToken);
  return { token: res.session_token, account: res.account, refreshToken: res.refresh_token };
}

/** Revoke a stored refresh token server-side (opt-out of "keep me signed in" / sign-out). Best-effort: a
 *  network failure is swallowed so the caller can still clear local state. Idempotent server-side. */
export async function revokeRefresh(api: AccountsApi, refreshToken: string): Promise<void> {
  try {
    await api.revokeRefresh(refreshToken);
  } catch (err) {
    console.error('wiser-auth: refresh-token revoke failed (clear local copy anyway)', err);
  }
}

/** Finish a new-device login started by `logIn` (after a `DeviceChallengeRequiredError`): re-prove the
 *  password + confirm the emailed PIN for THIS device → an unlocked session. */
export async function completeDeviceChallenge(
  api: AccountsApi,
  email: string,
  password: string,
  pin: string,
  keepSignedIn = false,
): Promise<UnlockedAccount> {
  const deviceId = await getOrCreateDeviceId();
  if (!deviceId) throw new Error('this device cannot be verified (no local storage)');
  const { kdf_salt_b64 } = await api.lookup(email);
  const verifier = await deriveVerifier(password, fromBase64(kdf_salt_b64));
  const res = await api.deviceChallenge({
    email,
    auth_verifier_b64: toBase64(verifier),
    device_id: deviceId,
    pin: pin.trim(),
    issue_refresh: keepSignedIn,
  });
  const keys = await unlock(password, res.account);
  return { token: res.session_token, account: res.account, keys, refreshToken: res.refresh_token };
}

/** OFFLINE unlock — password + cached blobs → keys, no server. For apps that persist an on-device session. */
export async function unlock(password: string, blobs: AccountBlobs): Promise<UnlockedKeys> {
  const kek = await deriveKek(password, fromBase64(blobs.kdf_salt_b64));
  const masterKey = await unwrapMasterKey(fromBase64(blobs.wrapped_master_key_b64), kek);
  return keysFromMasterKey(masterKey, blobs);
}

/** Given an already-recovered master key (e.g. from a device PIN), unwrap the private key from the cached
 *  blobs → the full key set. Shared by password unlock and any device-PIN unlock. */
export async function keysFromMasterKey(masterKey: Uint8Array, blobs: AccountBlobs): Promise<UnlockedKeys> {
  const privateKey = await unwrapPrivateKey(fromBase64(blobs.wrapped_private_key_b64), masterKey);
  return { masterKey, publicKey: fromBase64(blobs.public_key_b64), privateKey };
}
